import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { useAuth } from "@/contexts/auth-context";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CircleHelp } from "lucide-react";

interface AccountManagerOption {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

interface IndustryOption {
  id: number;
  name: string;
}

interface EquipmentTypeOption {
  id: number;
  name: string;
  slug: string;
  display_order: number;
}

interface ProvisionResponse {
  id: number;
  name: string;
  slug: string;
  application_link_url?: string | null;
}

export function StaffCreateOrgPage() {
  const { user } = useAuth();
  const { setViewingOrgId } = useViewingOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminFirst, setAdminFirst] = useState("");
  const [adminLast, setAdminLast] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState("");
  const [selectedManagerIds, setSelectedManagerIds] = useState<number[]>([]);
  /** Additional org dealer points % on top of lender points in prequal (optional; 0 if blank). */
  const [dealerPoints, setDealerPoints] = useState("");
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>([]);
  const [selectedEquipmentTypeIds, setSelectedEquipmentTypeIds] = useState<number[]>([]);
  const [notificationPresetSlug, setNotificationPresetSlug] = useState("full");
  const [notificationPresetInfoSlug, setNotificationPresetInfoSlug] = useState<string | null>(null);

  const [showPrequalsAfterSubmit, setShowPrequalsAfterSubmit] = useState(false);
  /** When false, hide simple interest % and APR on prequal UIs (payment/term unchanged). Independent of showing offers after sign. */
  const [showPrequalInterestAndApr, setShowPrequalInterestAndApr] = useState(true);
  const [sendUploadLinkEmail, setSendUploadLinkEmail] = useState(false);

  const [enableEmailFollowups, setEnableEmailFollowups] = useState(true);
  const [welcomeVideoUrl, setWelcomeVideoUrl] = useState("");
  const [howToVideoUrl, setHowToVideoUrl] = useState("");
  const [checkInVideoUrl, setCheckInVideoUrl] = useState("");
  const [lastTouchVideoUrl, setLastTouchVideoUrl] = useState("");
  const [loomError, setLoomError] = useState<string | null>(null);

  const isSuperAdmin = user?.role === "SUPERADMIN";

  const { data: accountManagers = [], isLoading: amLoading } = useQuery({
    queryKey: ["staff-account-managers"],
    queryFn: async () => {
      const { data } = await customInstance.get<AccountManagerOption[]>(
        "/api/organizations/staff-account-managers/"
      );
      return Array.isArray(data) ? data : [];
    },
    enabled: !!isSuperAdmin,
  });

  const { data: intakeData, isLoading: intakeLoading } = useQuery({
    queryKey: ["intake-data-staff-create-org"],
    queryFn: async () => {
      const { data } = await customInstance.get<{
        industries: IndustryOption[];
        equipment_types: EquipmentTypeOption[];
      }>("/api/intake/data/");
      return data;
    },
  });

  const industries = intakeData?.industries ?? [];
  const equipmentTypes = useMemo(() => {
    const list = intakeData?.equipment_types ?? [];
    return [...list].sort((a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name));
  }, [intakeData?.equipment_types]);

  const { data: notificationPresetCatalog = [], isLoading: presetsLoading } = useQuery({
    queryKey: ["staff-notification-presets"],
    queryFn: async () => {
      const { data } = await customInstance.get<NotificationPresetOption[]>(
        "/api/organizations/staff-notification-presets/"
      );
      return Array.isArray(data) ? data : [];
    },
  });

  useEffect(() => {
    if (!notificationPresetCatalog.length) return;
    const slugs = new Set(notificationPresetCatalog.map((p) => p.slug));
    if (!slugs.has(notificationPresetSlug)) {
      setNotificationPresetSlug("full");
    }
  }, [notificationPresetCatalog, notificationPresetSlug]);

  const provisionMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        contact_email: contactEmail.trim().toLowerCase(),
        contact_phone: contactPhone.trim(),
        admin_email: adminEmail.trim().toLowerCase(),
        admin_first_name: adminFirst.trim(),
        admin_last_name: adminLast.trim(),
        admin_password: adminPassword,
        enable_email_followups: enableEmailFollowups,
      };
      if (enableEmailFollowups) {
        payload.welcome_video_url = welcomeVideoUrl.trim() || null;
        payload.how_to_video_url = howToVideoUrl.trim() || null;
        payload.check_in_video_url = checkInVideoUrl.trim() || null;
        payload.last_touch_video_url = lastTouchVideoUrl.trim() || null;
      }
      if (isSuperAdmin) {
        payload.account_manager_ids = selectedManagerIds;
      }
      payload.show_prequals_after_submit = showPrequalsAfterSubmit;
      payload.show_prequal_interest_and_apr = showPrequalInterestAndApr;
      payload.send_upload_link_email = sendUploadLinkEmail;
      const dpTrim = dealerPoints.trim();
      payload.dealer_points = dpTrim === "" ? 0 : parseFloat(dpTrim);
      payload.allowed_industries = selectedIndustryIds;
      payload.allowed_equipment_types = selectedEquipmentTypeIds;
      payload.notification_preset = notificationPresetSlug;
      const { data } = await customInstance.post<ProvisionResponse>(
        "/api/organizations/staff-provision/",
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Organization "${data.name}" created.`);
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      queryClient.invalidateQueries({ queryKey: ["organization", data.id] });
      setViewingOrgId(data.id);
      navigate("/settings/org");
    },
    onError: (err: unknown) => {
      const ax = err as {
        response?: { data?: Record<string, string[] | string> };
      };
      const d = ax.response?.data;
      if (d && typeof d === "object") {
        const parts: string[] = [];
        for (const [k, v] of Object.entries(d)) {
          if (Array.isArray(v)) parts.push(`${k}: ${v.join(", ")}`);
          else if (typeof v === "string") parts.push(v);
        }
        toast.error(parts.join(" ") || "Could not create organization.");
      } else {
        toast.error("Could not create organization.");
      }
    },
  });

  const toggleManager = (id: number) => {
    setSelectedManagerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleIndustry = (id: number) => {
    setSelectedIndustryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleEquipmentType = (id: number) => {
    setSelectedEquipmentTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const loomRe = /^https:\/\/(www\.)?loom\.com\/(share|embed)\/[A-Za-z0-9]+(\?.*)?$/;
    const loomVals = [
      welcomeVideoUrl.trim(),
      howToVideoUrl.trim(),
      checkInVideoUrl.trim(),
      lastTouchVideoUrl.trim(),
    ].filter(Boolean);
    if (enableEmailFollowups) {
      const bad = loomVals.find((v) => !loomRe.test(v));
      if (bad) {
        setLoomError(
          "One or more Loom URLs are invalid. Use https://www.loom.com/share/<id> (or /embed/<id>)."
        );
        return;
      }
    }
    setLoomError(null);
    if (adminPassword !== adminPasswordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (isSuperAdmin && selectedManagerIds.length < 1) {
      toast.error("Select at least one DD Account Manager.");
      return;
    }
    const dpTrim = dealerPoints.trim();
    if (dpTrim !== "") {
      const n = parseFloat(dpTrim);
      if (!Number.isFinite(n)) {
        toast.error("Additional dealer points must be a valid number.");
        return;
      }
      if (n < 0 || n > 100) {
        toast.error("Additional dealer points must be between 0 and 100.");
        return;
      }
    }
    provisionMutation.mutate();
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create new organization</h1>
          <p className="text-sm text-gray-600 mt-1">
            Creates an active dealer organization, assigns account managers, and adds an organization
            admin with the password you set. The admin receives a welcome email with login details.
          </p>
        </div>
        <Link to="/apps">
          <Button type="button" variant="outline" size="sm">
            Back to applications
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization & admin</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="org-name">Organization name</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="organization"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Organization contact email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Organization contact phone (10 digits)</Label>
              <Input
                id="contact-phone"
                type="tel"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                required
                autoComplete="tel"
                placeholder="e.g. 5551234567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dealer-points" title="5 points are already built in, you can add more">
                Additional dealer points (%)
              </Label>
              <Input
                id="dealer-points"
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={dealerPoints}
                onChange={(e) => setDealerPoints(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-gray-500">
                5 points are already built in, you can add more.
              </p>
            </div>

            <div className="space-y-3 border-t pt-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Prequalification &amp; post-submit</p>
                <p className="text-xs text-gray-500 mt-1">
                  When applicants complete and sign the application (e.g. in a modal), choose whether they see prequalified
                  offers or only a thank-you message. Prequal runs after sign (credit pull when allowed); these settings control
                  what applicants see and whether they receive an upload-documents email. Org admins can change these later in
                  Organization settings.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPrequalsAfterSubmit}
                  onChange={(e) => setShowPrequalsAfterSubmit(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Show prequalified offers to applicants after they sign</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPrequalInterestAndApr}
                  onChange={(e) => setShowPrequalInterestAndApr(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">
                  Show simple interest rate and APR on prequal offers
                </span>
              </label>
              <p className="text-xs text-gray-500 -mt-2 ml-6">
                Payment and term still display when unchecked. Independent of whether applicants see offers after they
                sign. Super Admin and DD Account Manager can change this later in Organization settings.
              </p>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendUploadLinkEmail}
                  onChange={(e) => setSendUploadLinkEmail(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">
                  Email upload-documents link to customers after prequal selection or manual review
                </span>
              </label>
            </div>

            <div className="space-y-3 border-t pt-4">
              <div>
                <Label className="text-sm font-medium">Organization admin — notification preset</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Sets the first organization admin&apos;s email / in-app notification defaults. They can change
                  this later under their account.
                </p>
              </div>
              {presetsLoading ? (
                <p className="text-sm text-gray-500">Loading notification options…</p>
              ) : notificationPresetCatalog.length === 0 ? (
                <p className="text-sm text-amber-700">
                  Could not load presets. Try refreshing; default &quot;full&quot; will be used if you continue.
                </p>
              ) : (
                <ul className="space-y-3">
                  {notificationPresetCatalog.map((p) => (
                    <li
                      key={p.slug}
                      className="rounded-md border border-gray-200 bg-gray-50/50 px-3 py-2.5 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <input
                          type="radio"
                          name="notification-preset"
                          id={`preset-${p.slug}`}
                          checked={notificationPresetSlug === p.slug}
                          onChange={() => setNotificationPresetSlug(p.slug)}
                          className="mt-1 rounded-full border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`preset-${p.slug}`} className="cursor-pointer block">
                            <span className="text-sm font-medium text-gray-900">{p.label}</span>
                            <span className="block text-xs text-gray-500 mt-0.5">{p.category}</span>
                            <span className="block text-xs text-gray-600 mt-1">{p.summary}</span>
                          </label>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 p-1 rounded hover:bg-gray-200 text-gray-600"
                          title="What’s included"
                          aria-expanded={notificationPresetInfoSlug === p.slug}
                          aria-label={`Details: ${p.label}`}
                          onClick={() =>
                            setNotificationPresetInfoSlug((cur) => (cur === p.slug ? null : p.slug))
                          }
                        >
                          <CircleHelp className="w-4 h-4" aria-hidden />
                        </button>
                      </div>
                      {notificationPresetInfoSlug === p.slug && (
                        <ul className="text-xs text-gray-600 ml-6 list-disc space-y-1 pb-1">
                          {p.bullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-3 border-t pt-4">
              <div>
                <Label className="text-sm font-medium">Email follow-ups</Label>
                <p className="text-xs text-gray-500 mt-1">
                  Optional onboarding cadence that sends 3 follow-up emails after account creation.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableEmailFollowups}
                  onChange={(e) => setEnableEmailFollowups(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Enable onboarding email follow-ups</span>
              </label>

              {enableEmailFollowups && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="welcome-video" className="text-xs">
                        T+0 Welcome video (Loom URL, optional)
                      </Label>
                      <Input
                        id="welcome-video"
                        value={welcomeVideoUrl}
                        onChange={(e) => setWelcomeVideoUrl(e.target.value)}
                        placeholder="https://www.loom.com/share/..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="howto-video" className="text-xs">
                        T+1 How-to video (Loom URL, optional)
                      </Label>
                      <Input
                        id="howto-video"
                        value={howToVideoUrl}
                        onChange={(e) => setHowToVideoUrl(e.target.value)}
                        placeholder="https://www.loom.com/share/..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="checkin-video" className="text-xs">
                        T+5 Check-in video (Loom URL, optional)
                      </Label>
                      <Input
                        id="checkin-video"
                        value={checkInVideoUrl}
                        onChange={(e) => setCheckInVideoUrl(e.target.value)}
                        placeholder="https://www.loom.com/share/..."
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="lasttouch-video" className="text-xs">
                        T+14 Last touch video (Loom URL, optional)
                      </Label>
                      <Input
                        id="lasttouch-video"
                        value={lastTouchVideoUrl}
                        onChange={(e) => setLastTouchVideoUrl(e.target.value)}
                        placeholder="https://www.loom.com/share/..."
                      />
                    </div>
                  </div>
                  {loomError && <p className="text-sm text-red-600">{loomError}</p>}
                  {!welcomeVideoUrl.trim() &&
                    !howToVideoUrl.trim() &&
                    !checkInVideoUrl.trim() &&
                    !lastTouchVideoUrl.trim() && (
                      <p className="text-xs text-amber-700">
                        If enabled with no video URLs, dealers will receive 4 text-only follow-up emails.
                      </p>
                    )}
                </div>
              )}
            </div>

            {isSuperAdmin && (
              <div className="space-y-2">
                <Label>DD Account Managers (required)</Label>
                <p className="text-xs text-gray-500">
                  At least one must be selected. They will be assigned to this organization.
                </p>
                {amLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : accountManagers.length === 0 ? (
                  <p className="text-sm text-amber-700">No active DD Account Managers found.</p>
                ) : (
                  <ul className="border rounded-md divide-y max-h-48 overflow-y-auto">
                    {accountManagers.map((am) => (
                      <li key={am.id} className="flex items-center gap-2 px-3 py-2">
                        <input
                          type="checkbox"
                          id={`am-${am.id}`}
                          checked={selectedManagerIds.includes(am.id)}
                          onChange={() => toggleManager(am.id)}
                          className="rounded border-gray-300"
                        />
                        <label htmlFor={`am-${am.id}`} className="text-sm cursor-pointer flex-1">
                          {am.full_name}{" "}
                          <span className="text-gray-500">({am.email})</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!isSuperAdmin && (
              <p className="text-sm text-gray-600 bg-gray-50 border rounded-md px-3 py-2">
                You will be assigned as a DD Account Manager for this organization automatically.
              </p>
            )}

            <div className="border-t pt-5 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-900">Industries &amp; equipment types (optional)</p>
                <p className="text-xs text-gray-500 mt-1">
                  Limit which industries and equipment types this dealer can use. Leave all unchecked to allow
                  everything (same as Organization settings).
                </p>
              </div>
              {intakeLoading ? (
                <p className="text-sm text-gray-500">Loading industries and equipment types…</p>
              ) : (
                <>
                  <div>
                    <Label className="text-sm font-medium">Industries served</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {industries.length === 0 ? (
                        <p className="text-sm text-gray-500">No industries loaded.</p>
                      ) : (
                        industries.map((ind) => (
                          <label key={ind.id} className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedIndustryIds.includes(ind.id)}
                              onChange={() => toggleIndustry(ind.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{ind.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Equipment types served</Label>
                    <div className="mt-2 flex flex-wrap gap-2 max-h-64 overflow-y-auto border rounded-md p-2">
                      {equipmentTypes.length === 0 ? (
                        <p className="text-sm text-gray-500">No equipment types loaded.</p>
                      ) : (
                        equipmentTypes.map((et) => (
                          <label key={et.id} className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedEquipmentTypeIds.includes(et.id)}
                              onChange={() => toggleEquipmentType(et.id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm">{et.name}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="border-t pt-4 space-y-4">
              <p className="text-sm font-medium text-gray-900">Organization admin (first user)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-first">First name</Label>
                  <Input
                    id="admin-first"
                    value={adminFirst}
                    onChange={(e) => setAdminFirst(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin-last">Last name</Label>
                  <Input
                    id="admin-last"
                    value={adminLast}
                    onChange={(e) => setAdminLast(e.target.value)}
                    required
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-email">Admin email (login)</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Initial password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password-2">Confirm password</Label>
                <Input
                  id="admin-password-2"
                  type="password"
                  value={adminPasswordConfirm}
                  onChange={(e) => setAdminPasswordConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={provisionMutation.isPending}>
                {provisionMutation.isPending ? "Creating…" : "Create organization"}
              </Button>
              <Link to="/apps">
                <Button type="button" variant="outline">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
